import api from './api';

export type HrRequestType = 'overtime' | 'lateness_excuse' | 'reimbursement' | 'visit'
    | 'annual_leave' | 'sick_leave' | 'permission_leave' | 'maternity_leave';
export type HrRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface HrRequestPayload {
    workspaceId: string;
    teamId: string;
    type: HrRequestType;
    fromAt: string;
    toAt?: string;
    payload: Record<string, any>;
    amount?: number;
}

export interface ReimbursementOcrResult {
    amount: number;
    category: 'transport' | 'makan' | 'visit' | 'lainnya';
    merchantName: string | null;
    transactionDate: string | null;
    currency: string;
    taxAmount: number | null;
    confidence: number;
    reason: string;
    rawText: string;
}

export const hrRequestService = {
    list: (params: { workspaceId: string; type?: HrRequestType; status?: HrRequestStatus; teamId?: string; from?: string; to?: string; scope?: 'mine' | 'team' | 'all'; limit?: number }) =>
        api.get('/hr-requests', { params }),
    get: (id: string) => api.get(`/hr-requests/${id}`),
    create: (data: HrRequestPayload) => api.post('/hr-requests', data),
    ocrReimbursement: (workspaceId: string, file: File) => {
        const form = new FormData();
        form.set('workspaceId', workspaceId);
        form.set('file', file);
        return api.post<ReimbursementOcrResult>('/hr-requests/reimbursement/ocr', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    cancel: (id: string) => api.post(`/hr-requests/${id}/cancel`),
    // Approve / reject — backend menentukan otorisasi (single-level admin tim ATAU
    // chained: hanya current approver / delegate-nya yang boleh approve level ini).
    approve: (id: string, note?: string) => api.post(`/hr-requests/${id}/approve`, note ? { note } : {}),
    reject: (id: string, note: string) => api.post(`/hr-requests/${id}/reject`, { note }),
    // Rekap cuti per workspace (admin/team-visible). View pegawai memakai list scope:'mine' untuk riwayat
    // dirinya; endpoint ini dipakai bila user punya visibilitas tim untuk mengambil kuota tahunannya.
    leaveRecap: (workspaceId: string, params: { from?: string; to?: string; teamId?: string; status?: 'approved' | 'pending' | 'all' } = {}) =>
        api.get(`/hr-requests/leave-recap/${workspaceId}`, { params }),
};

// Satu level dalam rantai approval (snapshot di approval_steps_json).
export interface ApprovalStep {
    approverRole?: string;
    approverUserId?: string;
    approverName?: string;
    status?: 'pending' | 'approved' | 'rejected';
}

// Shift-swap: pegawai minta tukar shift di satu tanggal dengan rekan.
export const shiftSwapService = {
    request: (data: { workspaceId: string; targetUserId: string; swapDate: string; reason?: string }) =>
        api.post('/attendance/shift-swaps', data),
    list: (params: { workspaceId: string; status?: HrRequestStatus; scope?: 'mine' | 'all' }) =>
        api.get('/attendance/shift-swaps', { params }),
    approve: (id: string) => api.post(`/attendance/shift-swaps/${id}/approve`),
    reject: (id: string) => api.post(`/attendance/shift-swaps/${id}/reject`),
};

// Daftar rekan kerja di workspace (untuk picker target swap).
export const memberService = {
    list: (workspaceId: string) => api.get(`/workspaces/${workspaceId}/members`),
};

export const LEAVE_TYPES: HrRequestType[] = ['sick_leave', 'permission_leave', 'annual_leave', 'maternity_leave'];

// Hari cuti inklusif (fromAt..toAt; tanpa toAt = 1 hari). Sama dengan perhitungan di endpoint /summary.
export function leaveDays(fromAt: string, toAt?: string | null): number {
    const f = new Date(fromAt); f.setHours(0, 0, 0, 0);
    const tt = toAt ? new Date(toAt) : new Date(fromAt); tt.setHours(0, 0, 0, 0);
    return Math.max(1, Math.floor((tt.getTime() - f.getTime()) / 86_400_000) + 1);
}
